import { FunctionComponent, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { CreateKidDocumentValues } from '../CreateKidDocumentModal';
import GenericDropdown from '../../../../../ui/GenericDropDown';

interface DuplicateDocumentSelectorProps {
  targetFundId: string;
  fundsMap: Map<string, any>;
  shareClassesByFund: Map<string, any[]>;
  allShareClasses: any[] | undefined;
}

const DuplicateDocumentSelector: FunctionComponent<DuplicateDocumentSelectorProps> = ({
  targetFundId,
  fundsMap,
  shareClassesByFund,
  allShareClasses,
}) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateKidDocumentValues>();
  const template_share_class_id = watch('template_share_class_id');

  const targetFund = fundsMap.get(targetFundId);
  const targetFundLabel = targetFund
    ? `${targetFund.fund_name} — ${targetFund.fund_name_full}`
    : targetFundId;

  const shareClassesForFund = useMemo(
    () => shareClassesByFund.get(targetFundId) || [],
    [shareClassesByFund, targetFundId],
  );

  useEffect(() => {
    setValue('template_fund_id', targetFundId, { shouldDirty: false });
    setValue('template_fund_name', targetFund?.fund_name_full, {
      shouldDirty: false,
    });

    if (!template_share_class_id) return;

    const shareClass = allShareClasses?.find(
      (entry) => entry.share_class_id === template_share_class_id,
    );
    if (shareClass?.fund_id !== targetFundId) {
      setValue('template_share_class_id', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('template_share_class_name', undefined, { shouldDirty: false });
    }
  }, [
    allShareClasses,
    setValue,
    targetFund?.fund_name_full,
    targetFundId,
    template_share_class_id,
  ]);

  return (
    <div className="space-y-4 rounded-md border border-dashed border-gray-300 p-3">
      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Fund
        </label>
        <div className="w-96 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
          {targetFundLabel}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Duplicate from a share class in this fund only
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Select Share Class to Duplicate <span className="text-red-500">*</span>
        </label>
        <GenericDropdown
          data={shareClassesForFund}
          selected={
            shareClassesForFund.find(
              (shareClass) => shareClass.share_class_id === template_share_class_id,
            ) || null
          }
          setSelected={(shareClass) => {
            setValue('template_share_class_id', shareClass.share_class_id, {
              shouldDirty: true,
              shouldValidate: true,
            });
            setValue('template_share_class_name', shareClass.share_class_name_full, {
              shouldDirty: false,
            });
            setValue('template_fund_name', targetFund?.fund_name_full, {
              shouldDirty: false,
            });
            setValue('template_fund_id', targetFundId, { shouldDirty: false });
          }}
          getDisplayValue={(shareClass) => shareClass.share_class_name_full}
          label={
            shareClassesForFund.find(
              (shareClass) => shareClass.share_class_id === template_share_class_id,
            )?.share_class_name_full ?? 'Select a share class...'
          }
          searchBar
          className="w-96 justify-between bg-white text-sm text-black cursor-pointer items-center rounded-md border-gray-200 data-[hover]:bg-gray-50 border p-2 text-left  data-[open]:bg-gray-50"
          dropdownClassName="max-h-64 overflow-y-auto w-96"
        />
        <p className="mt-1 text-xs text-gray-500">
          The KID document structure and formatting will be copied from this share class
        </p>
        {errors.template_share_class_id && (
          <p className="mt-1 text-xs text-red-600">
            {errors.template_share_class_id.message as string}
          </p>
        )}
      </div>
    </div>
  );
};

export default DuplicateDocumentSelector;
